// @ts-nocheck
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { ReactComponent as UploadFileRoundedIcon } from '../../../../assets/Icons/lucideIcons/file-up.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  LinearProgress,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { ReactComponent as InfoOutlinedIcon } from '../../../../assets/Icons/lucideIcons/info.svg';
import { ReactComponent as CheckCircleIcon } from '../../../../assets/Icons/lucideIcons/circle-check.svg';

import { isValueEmpty, getErrorHelperText } from '../../../../utils/Utils';
import {
  useDocumentTagsQuery,
  useDeleteDocumentMutation,
} from '../../../../features/document/api/documentInventoryQueries';
import Multiselect from '../../../../components/Common/Multiselect';
import { useUploadDocumentContext } from '../../../../context/DocumentUpload/UploadDocumentContext';
import SwalToast from '../../swalToast';
import REGEX from '../../../../const/Regex';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';

/**
 * Document field component for uploading and viewing documents
 * @param {Object} props - Component props
 * @param {Object} props.value - Current document value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @returns {React.ReactElement} The DocumentFieldComponent
 */
const DocumentFieldComponent = ({
  value,
  onChange,
  label,
  required = false,
  entityDetailsFormikForm,
  attribute,
  entityData,
  configColumn = false,
}) => {
  const { data: tagsResponse } = useDocumentTagsQuery();

  const allTags = tagsResponse?.tags || [];

  const { mutateAsync: deleteDocument } = useDeleteDocumentMutation();
  const { uploadFileForAttribute } = useAttribute();

  const {
    handleFileSelection,
    handleCompleteUpload,
    handleStopUpload,
    addBatch,
    removeBatch,
    uploadProgress,
    uploadErrors,
    activeBatches,
    clearUploadError,
  } = useUploadDocumentContext();
  // Find any active upload for this attribute AND this entity (runs once during first render)
  const [restoredUpload] = useState(() => {
    for (const batch of activeBatches || []) {
      const isEntityMatch =
        String(batch?.entityId) === String(entityData?.entity_id) ||
        String(batch?.uniqueId) === String(entityData?.unique_id);

      if (!isEntityMatch) continue;

      const file = (batch.data || []).find(
        (f) => f.attribute_id === attribute?.id && f.document_from_attribute
      );
      if (file) return { file, batch };
    }
    return null;
  });

  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [files, setFiles] = useState(() => {
    if (restoredUpload) {
      const fileObj = restoredUpload.file.file || {
        name: restoredUpload.file.document_name || 'Uploading...',
      };
      return [fileObj];
    }
    return [];
  });
  const [isFileUploaded, setIsFileUploaded] = useState(!!restoredUpload);
  const [isNewFileUploaded, setIsNewFileUploaded] = useState(!!restoredUpload);
  const [previousUploadedFileDetails, setPreviousUploadedFileDetails] =
    useState(() => {
      if (isValueEmpty(value)) return [];
      // If it's an array of empty objects, treat as empty
      if (Array.isArray(value) && value.length > 0 && !value[0]?.document_name)
        return [];
      return value;
    });

  // New Chunked Upload States
  const [currentUploadKey, setCurrentUploadKey] = useState(
    restoredUpload?.file?.key || null
  );
  const [currentBatchId, setCurrentBatchId] = useState(
    restoredUpload?.batch?.batchId || null
  );
  const [isProcessing, setIsProcessing] = useState(() => {
    if (restoredUpload) {
      const progress = uploadProgress?.[restoredUpload.file.key] || 0;
      return progress === 100 && !uploadErrors?.[restoredUpload.file.key];
    }
    return false;
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(() => {
    return restoredUpload ? !!uploadErrors?.[restoredUpload.file.key] : false;
  });
  const [localProgress, setLocalProgress] = useState(0);
  const completedDocIdRef = useRef(null);
  const currentUploadKeyRef = useRef(restoredUpload?.file?.key || null);
  const valueRef = useRef(value);

  // Keep valueRef in sync with the latest value prop
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const isDisabled = useMemo(() => !attribute?.is_editable, [attribute]);
  const existingTags = useMemo(() => value?.[0]?.tags || [], [value]);
  const currentDocumentID = useMemo(() => value?.[0]?.document_id, [value]);

  const hasAttachedFile = useMemo(
    () =>
      (files?.length > 0 && isFileUploaded) ||
      previousUploadedFileDetails?.length > 0,
    [files, isFileUploaded, previousUploadedFileDetails]
  );

  const hasError = useMemo(() => {
    return Boolean(
      getErrorHelperText(
        entityDetailsFormikForm?.errors?.[attribute?.id],
        entityDetailsFormikForm?.touched?.[attribute?.id]
      )
    );
  }, [entityDetailsFormikForm, attribute?.id]);

  const helperText = useMemo(() => {
    return getErrorHelperText(
      entityDetailsFormikForm?.errors?.[attribute?.id],
      entityDetailsFormikForm?.touched?.[attribute?.id]
    );
  }, [entityDetailsFormikForm, attribute?.id]);

  const textColor = useMemo(() => {
    if (isDisabled) return 'other.grey2';
    if (hasError) return 'error.main';
    return 'text.primary';
  }, [isDisabled, hasError]);

  const markTouched = useCallback(
    (shouldValidate = false) => {
      entityDetailsFormikForm?.setFieldTouched(
        attribute?.id,
        true,
        shouldValidate
      );
    },
    [entityDetailsFormikForm, attribute?.id]
  );

  const { pluginConfig } = useSelector((state) => state?.tenant);

  const sharepointPluginConfig = useMemo(
    () => (pluginConfig?.Sharepoint ? pluginConfig?.Sharepoint() : {}),
    [pluginConfig]
  );

  const maxLimitMB = useMemo(() => {
    return attribute?.document_size_limit || 350;
  }, [attribute, pluginConfig]);

  const accept = useMemo(() => {
    if (
      !attribute?.supported_file_types ||
      attribute.supported_file_types.length === 0
    ) {
      return undefined;
    }
    const acc = {};

    attribute.supported_file_types.forEach((type) => {
      if (type?.value) {
        acc[`.${String(type?.value).toLowerCase()}`] = [];
      }
    });

    return Object.keys(acc).length > 0 ? acc : undefined;
  }, [attribute?.supported_file_types]);

  const handleChangeIsFileUploaded = useCallback(
    (isFileUploadedValue) => {
      setIsFileUploaded(isFileUploadedValue);
    },
    [setIsFileUploaded]
  );

  const initiateUpload = useCallback(
    (fileToUpload) => {
      if (configColumn) {
        // Stop any existing upload before starting a new one
        if (currentUploadKey) {
          handleStopUpload(currentUploadKey);
        }

        const key = crypto.randomUUID();
        setCurrentUploadKey(key);
        currentUploadKeyRef.current = key;
        setIsProcessing(true);
        setIsSuccess(false);
        setUploadFailed(false);
        setIsNewFileUploaded(true);
        handleChangeIsFileUploaded(true);
        setLocalProgress(0);
        completedDocIdRef.current = null;

        const ext = fileToUpload?.name?.split('.')?.pop() || '';
        const nameWithoutExt = fileToUpload?.name?.replace(
          REGEX.REMOVE_FILE_EXTENSION,
          ''
        );

        // Initial placeholder
        onChange([
          { document_name: nameWithoutExt, document_type: ext, tags: [] },
        ]);

        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append(
          'data',
          JSON.stringify({
            entity_id: entityData?.entity_id,
            entity_type: entityData?.entity_type,
            attribute_id: attribute?.id,
            tags: [],
          })
        );

        uploadFileForAttribute(formData)
          .then((res) => {
            if (key !== currentUploadKeyRef.current) return;
            const data = res?.data;
            if (data) {
              const mappedValue = [
                {
                  tags: data.tags || [],
                  document_id: data.document_id,
                  document_name: data.document_name,
                  document_type: data.file_type,
                },
              ];
              onChange(mappedValue);
              setIsSuccess(true);
              setIsProcessing(false);
              setLocalProgress(100);
              setFiles([fileToUpload]);
              completedDocIdRef.current = data.document_id;
              if (required) {
                entityDetailsFormikForm?.setFieldError(attribute.id, undefined);
              }
            }
          })
          .catch((err) => {
            if (key !== currentUploadKeyRef.current) return;
            console.error('Attribute document upload error', err);
            setIsProcessing(false);
            setUploadFailed(true);
            if (required) {
              entityDetailsFormikForm?.setFieldError(
                attribute.id,
                'Upload failed. Please retry.'
              );
            }
          });
        return;
      }

      // Original Chunked Upload Logic
      // Stop any existing upload before starting a new one
      if (currentUploadKey) {
        handleStopUpload(currentUploadKey);
        clearUploadError(currentUploadKey); // Clear the cancelled error state so it doesn't flicker on the UI
      }
      if (currentBatchId) {
        removeBatch(currentBatchId);
      }

      if (isNewFileUploaded && currentDocumentID) {
        deleteDocument({ document_id: currentDocumentID });
      }
      const key = crypto.randomUUID();
      const batchId = crypto.randomUUID();
      setCurrentUploadKey(key);
      currentUploadKeyRef.current = key;
      setCurrentBatchId(batchId);
      setIsProcessing(false);
      setIsSuccess(false);
      setUploadFailed(false);
      setIsNewFileUploaded(true);
      handleChangeIsFileUploaded(true);
      completedDocIdRef.current = null;

      const ext = fileToUpload?.name?.split('.')?.pop() || '';
      const nameWithoutExt = fileToUpload?.name?.replace(
        REGEX.REMOVE_FILE_EXTENSION,
        ''
      );
      onChange([
        { document_name: nameWithoutExt, document_type: ext, tags: [] },
      ]);

      if (required) {
        entityDetailsFormikForm?.setFieldError(
          attribute.id,
          'Upload in progress...'
        );
      } else {
        entityDetailsFormikForm?.setFieldError(attribute.id, undefined);
      }

      const batchData = {
        entityId: entityData?.entity_id,
        enitityType: entityData?.entity_type,
        requestType: entityData?.request_type,
        uniqueId: entityData?.unique_id,
      };

      const fileMetadata = {
        file: fileToUpload,
        key,
        document_name: fileToUpload.name,
        attribute_id: attribute?.id,
        document_from_attribute: true,
        entities: [],
        tags: value?.[0]?.tags || [],
        fileSize: fileToUpload?.size || 0,
      };

      addBatch(batchId, batchData, [fileMetadata]);
      handleFileSelection([fileMetadata]);

      // Call completeUpload in background — document_id will come from the dedicated SSE event
      handleCompleteUpload(batchId, [fileMetadata], batchData).catch((err) => {
        // Only trigger failure UI if this is still the active upload
        if (key !== currentUploadKeyRef.current) return;
        console.error('Attribute document upload completion error', err);
        setIsProcessing(false);
        setUploadFailed(true);
        if (required) {
          entityDetailsFormikForm?.setFieldError(
            attribute.id,
            'Upload failed. Please retry.'
          );
        }
      });
    },
    [
      isNewFileUploaded,
      currentDocumentID,
      deleteDocument,
      onChange,
      required,
      entityDetailsFormikForm,
      attribute?.id,
      entityData,
      addBatch,
      handleFileSelection,
      handleCompleteUpload,
      handleStopUpload,
      removeBatch,
      clearUploadError,
      handleChangeIsFileUploaded,
      value,
      currentUploadKey,
      currentBatchId,
      configColumn,
      uploadFileForAttribute,
    ]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    maxSize: maxLimitMB * 1024 * 1024,
    onDrop: () => {
      markTouched();
    },
    onDropAccepted: (selectedFiles) => {
      const file = selectedFiles[0];
      if (file.size > maxLimitMB * 1024 * 1024) {
        SwalToast({
          icon: 'error',
          title: `Max allowed file size is ${maxLimitMB} MB`,
        });
        setRejectedFiles([
          {
            file,
            errors: [{ code: 'file-too-large', message: 'File is too large' }],
          },
        ]);
        return;
      }
      setRejectedFiles([]);
      setFiles([file]);
      handleChangeIsFileUploaded(true);
      initiateUpload(file);
      markTouched();
    },
    onDropRejected: (_rejected) => {
      const errorIsSize = _rejected?.[0]?.errors?.some(
        (e) => e.code === 'file-too-large'
      );
      if (errorIsSize) {
        SwalToast({
          icon: 'error',
          title: `Max allowed file size is ${maxLimitMB} MB`,
        });
      }
      setRejectedFiles(_rejected);
      markTouched();
    },
    disabled: isDisabled,
    accept,
  });

  useEffect(() => {
    if (
      currentUploadKey &&
      uploadProgress?.[currentUploadKey] === 100 &&
      !isProcessing &&
      !isSuccess
    ) {
      if (currentUploadKey !== currentUploadKeyRef.current) return;
      if (!uploadErrors?.[currentUploadKey]) {
        setIsProcessing(true);
        if (required) {
          entityDetailsFormikForm?.setFieldError(
            attribute.id,
            'Processing Document...'
          );
        }
      }
    }
  }, [
    uploadProgress,
    currentUploadKey,
    isProcessing,
    isSuccess,
    uploadErrors,
    required,
    entityDetailsFormikForm,
    attribute?.id,
  ]);

  useEffect(() => {
    if (currentUploadKey && uploadErrors?.[currentUploadKey] && !uploadFailed) {
      if (currentUploadKey !== currentUploadKeyRef.current) return;
      setUploadFailed(true);
      setIsProcessing(false);
      if (required) {
        entityDetailsFormikForm?.setFieldError(
          attribute.id,
          'Upload failed. Please retry.'
        );
      }
    }
  }, [
    uploadErrors,
    currentUploadKey,
    uploadFailed,
    required,
    entityDetailsFormikForm,
    attribute?.id,
  ]);

  const handleClearRejectedFiles = useCallback(
    (e) => {
      e.stopPropagation();
      setRejectedFiles([]);
      handleChangeIsFileUploaded(false);
      onChange([]);
    },
    [setRejectedFiles, handleChangeIsFileUploaded, onChange]
  );

  /**
   * Handles the click event to clear the array of files.
   * Clears the array of files and stops the event propagation.
   *
   * @function
   * @name handleClearFiles
   * @param {Object} e - The click event object.
   * @returns {void}
   */
  /**
   * Clears the current files from the field state and, if a file was previously uploaded,
   * triggers its deletion from the server.
   * @function
   * @param {Event} e - The click event object.
   * @returns {void}
   */
  const handleClearFiles = useCallback(
    (e) => {
      e.stopPropagation();
      if (currentUploadKey) {
        handleStopUpload(currentUploadKey);
      }
      if (currentBatchId) {
        removeBatch(currentBatchId);
      }
      if (isNewFileUploaded && currentDocumentID) {
        deleteDocument({ document_id: currentDocumentID });
      }
      setFiles([]);
      handleChangeIsFileUploaded(false);
      setPreviousUploadedFileDetails([]);
      setCurrentUploadKey(null);
      currentUploadKeyRef.current = null;
      setCurrentBatchId(null);
      setIsProcessing(false);
      setIsSuccess(false);
      setUploadFailed(false);
      setIsNewFileUploaded(false);

      markTouched(false);
      onChange([]);
      // Set the Formik field value to empty and validate so the
      // mandatory-field error is triggered against the correct empty value.
      entityDetailsFormikForm?.setFieldValue(attribute?.id, [], true);
    },
    [
      currentUploadKey,
      currentBatchId,
      handleStopUpload,
      removeBatch,
      isNewFileUploaded,
      currentDocumentID,
      deleteDocument,
      markTouched,
      handleChangeIsFileUploaded,
      onChange,
      entityDetailsFormikForm,
      attribute?.id,
    ]
  );
  const supportedTypesLabel = useMemo(() => {
    if (
      Array.isArray(attribute?.supported_file_types) &&
      attribute.supported_file_types.length > 0
    ) {
      return attribute?.supported_file_types
        .map((t) => t?.label)
        .filter(Boolean)
        .join(', ');
    }
    return '';
  }, [attribute?.supported_file_types]);

  const renderFile = () => {
    if (isDragActive) {
      return <Typography align="center">Drop the file here...</Typography>;
    }

    if (rejectedFiles?.length > 0) {
      const rejectCode = rejectedFiles[0]?.errors?.[0]?.code;
      const rejectMsg =
        rejectCode === 'file-too-large'
          ? `File exceeds ${maxLimitMB}MB limit.`
          : 'Invalid File Type.';
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography align="center" color={textColor}>
            {rejectMsg}
          </Typography>
          <IconButton
            aria-label="Remove file."
            size="small"
            color="primary"
            onClick={handleClearRejectedFiles}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      );
    }

    if (files?.length > 0 && isFileUploaded) {
      let prog = 0;
      if (currentUploadKey) {
        prog = configColumn
          ? localProgress
          : uploadProgress?.[currentUploadKey] || 0;
      }
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            width: '100%',
            mt: 0.5,
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            <Typography
              color={textColor}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={files[0]?.name}
            >
              {files[0]?.name}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              {uploadFailed && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Failed
                </Typography>
              )}
              {!uploadFailed && isSuccess && (
                <CheckCircleIcon
                  width={20}
                  height={20}
                  title="Uploaded successfully"
                />
              )}
              <IconButton
                aria-label="Remove file."
                size="small"
                color={`${isDisabled ? 'other.grey2' : 'primary'}`}
                onClick={handleClearFiles}
                disabled={isDisabled}
                sx={{ padding: 0 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {!isSuccess && !uploadFailed && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <LinearProgress
                variant="determinate"
                value={prog}
                sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" sx={{ minWidth: 35 }}>
                {Math.round(prog)}%
              </Typography>
            </Box>
          )}
          {uploadFailed && (
            <Typography variant="caption" color="error">
              Upload failed. Please retry or clear file.
            </Typography>
          )}
        </Box>
      );
    }

    const hasPreviousFile =
      !isValueEmpty(previousUploadedFileDetails) &&
      previousUploadedFileDetails?.[0]?.document_name;

    if (hasPreviousFile && files?.length <= 0) {
      const documentName =
        previousUploadedFileDetails?.[0]?.document_name || '';
      const fileType =
        previousUploadedFileDetails?.[0]?.file_type ||
        previousUploadedFileDetails?.[0]?.document_type ||
        '';
      const fileName = fileType ? `${documentName}.${fileType}` : documentName;

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <Typography
            color={textColor}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={fileName}
          >
            {fileName}
          </Typography>
          <IconButton
            aria-label="Remove file."
            size="small"
            color={`${isDisabled ? 'other.grey2' : 'primary'}`}
            onClick={handleClearFiles}
            disabled={isDisabled}
            sx={{
              padding: 0,
              flexShrink: 0,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      );
    }

    return (
      <Typography align="center" color={textColor}>
        {supportedTypesLabel
          ? `Drag & drop file here.`
          : 'Drag & drop file here, or click to select file.'}
      </Typography>
    );
  };

  const renderTagInput = useCallback(
    (params) => (
      <TextField
        {...params}
        variant="outlined"
        label="Document tag(s)"
        placeholder={value?.[0]?.tags?.length ? '' : 'Select tags'}
        spellCheck={true}
        slotProps={{
          ...params?.slotProps,
          inputLabel: { ...params?.slotProps?.inputLabel, shrink: true },
        }}
      />
    ),
    [value]
  );

  const handleTagChange = useCallback(
    (value_) => {
      const prevValue = value?.[0] || {};
      onChange([
        {
          ...prevValue,
          tags: value_,
        },
      ]);
    },
    [onChange, value]
  );
  useEffect(() => {
    if (!isValueEmpty(value)) {
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        !value[0]?.document_name
      ) {
        setPreviousUploadedFileDetails([]);
      } else {
        setPreviousUploadedFileDetails(value);
      }
    } else {
      setPreviousUploadedFileDetails([]);
    }
  }, [value]);

  const allowedFileTypeInfo = useMemo(() => {
    return (
      <Tooltip
        title={'Allowed file types: ' + supportedTypesLabel}
        placement="right-end"
      >
        <InfoOutlinedIcon
          width={18}
          height={18}
          style={{
            cursor: 'pointer',
            verticalAlign: 'bottom',
            marginLeft: '8px',
          }}
        />
      </Tooltip>
    );
  }, [supportedTypesLabel]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Typography
          variant="body4"
          color={textColor}
          sx={{
            mb: 0.5,
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: 'error.main',
              }}
            >
              {' '}
              *
            </Typography>
          )}
          {supportedTypesLabel && allowedFileTypeInfo}
        </Typography>
        <Box
          sx={{
            cursor: isDisabled ? 'default' : 'pointer',
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            gap: 1,
            mr: 1,
          }}
        >
          {sharepointPluginConfig?.Attributes?.Local?.map((Component) => (
            <Box className="upload-document-attribute" key={Component?.name}>
              <Component
                attributeData={{
                  entityData,
                  attribute,
                }}
                handleChangeIsFileUploaded={handleChangeIsFileUploaded}
                isDisabled={isDisabled}
              />
            </Box>
          ))}
          <Box
            {...getRootProps({ className: 'dropzone' })}
            onBlur={markTouched}
            sx={{
              cursor: isDisabled ? 'default' : 'pointer',
              pointerEvents: isDisabled ? 'none' : 'auto',
              display: 'flex',
              justifyContent: 'start',
              alignItems: 'center',
              p: 1,
              minWidth: 0,
              overflow: 'hidden',
              flexGrow: 1,
            }}
          >
            <input {...getInputProps()} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                minWidth: 0,
              }}
            >
              <UploadFileRoundedIcon width={30} height={30} />
              <Box
                sx={{
                  flex: '1 1 0',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                {renderFile()}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box>
        {hasError && helperText && (
          <Typography
            variant="caption"
            sx={{
              color: 'error.main',
              mt: 0.5,
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          mt: 2,
          pr: 1,
        }}
      >
        <Multiselect
          id={`${attribute?.id}_doc_tags`}
          options={allTags?.map((option) => option)}
          value={existingTags}
          noOptionsMessage="No tags found."
          renderInput={renderTagInput}
          onChange={handleTagChange}
          disabled={isDisabled || rejectedFiles?.length > 0 || !hasAttachedFile}
          freeSolo={true}
          forcePopupIcon={true}
        />
      </Box>
    </>
  );
};

DocumentFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      document_name: PropTypes.string,
      document_type: PropTypes.string,
      file_type: PropTypes.string,
      document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tags: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
      ),
    })
  ),
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    is_editable: PropTypes.bool,
    name: PropTypes.string,
    document_default_tags: PropTypes.arrayOf(PropTypes.string),
    // Add other specific attribute properties as needed
    supported_file_types: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    document_size_limit: PropTypes.number,
  }).isRequired,
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
    unique_id: PropTypes.string,
    entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entityType: PropTypes.string,
    request_type: PropTypes.string,
  }),
  entityDetailsFormikForm: PropTypes.shape({
    setFieldTouched: PropTypes.func,
    setFieldValue: PropTypes.func,
    setFieldError: PropTypes.func,
    errors: PropTypes.shape({}),
    touched: PropTypes.shape({}),
  }),
  configColumn: PropTypes.bool,
};

export default DocumentFieldComponent;
